import { useCallback, useEffect, useRef, useState } from 'react';
import { hideResizeGuide, showResizeGuide } from '@/lib/resizeGuide';

const readStoredColumnWidths = (storageKey, defaultWidths) => {
  if (typeof window === 'undefined') return defaultWidths;

  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return defaultWidths;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return defaultWidths;

    return { ...defaultWidths, ...parsed };
  } catch {
    return defaultWidths;
  }
};

export const useResizableTableColumns = ({ storageKey, defaultWidths, minWidth = 72 }) => {
  const [columnWidths, setColumnWidths] = useState(() => readStoredColumnWidths(storageKey, defaultWidths));
  const resizeStateRef = useRef({ columnKey: null, startX: 0, startWidth: 0 });

  const persistColumnWidths = useCallback((nextWidths) => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(storageKey, JSON.stringify(nextWidths));
  }, [storageKey]);

  useEffect(() => {
    setColumnWidths((currentWidths) => ({ ...defaultWidths, ...currentWidths }));
  }, [defaultWidths]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!resizeStateRef.current.columnKey) return;

      const delta = event.clientX - resizeStateRef.current.startX;
      const nextWidth = Math.max(minWidth, Math.round(resizeStateRef.current.startWidth + delta));
      showResizeGuide(event.clientX);

      setColumnWidths((currentWidths) => {
        const nextWidths = {
          ...currentWidths,
          [resizeStateRef.current.columnKey]: nextWidth,
        };
        persistColumnWidths(nextWidths);
        return nextWidths;
      });
    };

    const handleMouseUp = () => {
      resizeStateRef.current = { columnKey: null, startX: 0, startWidth: 0 };
      hideResizeGuide();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      hideResizeGuide();
    };
  }, [minWidth, persistColumnWidths]);

  const startColumnResize = useCallback((columnKey, event) => {
    event.preventDefault();
    event.stopPropagation();

    resizeStateRef.current = {
      columnKey,
      startX: event.clientX,
      startWidth: columnWidths[columnKey] || defaultWidths[columnKey] || minWidth,
    };
    showResizeGuide(event.clientX);
  }, [columnWidths, defaultWidths, minWidth]);

  const getColumnStyle = useCallback((columnKey) => {
    const width = columnWidths[columnKey] || defaultWidths[columnKey] || minWidth;
    return { width, minWidth: width };
  }, [columnWidths, defaultWidths, minWidth]);

  return {
    columnWidths,
    getColumnStyle,
    startColumnResize,
  };
};

export default useResizableTableColumns;